import { Component, ErrorInfo, ReactNode } from "react";
import styled from "styled-components";
import Button from "../Button/Button";

const ErrorContainer = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	min-height: 50vh;
	padding: 2rem;
	text-align: center;
	gap: 1rem;
`;

const ErrorTitle = styled.h2`
	margin: 0;
`;

const ErrorMessage = styled.p`
	margin: 0;
	max-width: 36rem;
	color: ${({ theme }) => theme.colorCopyDark};
`;

const isChunkLoadError = (error: Error) => {
	const message = error.message || "";
	return (
		error.name === "ChunkLoadError" ||
		message.includes("Loading chunk") ||
		message.includes("Failed to fetch dynamically imported module") ||
		message.includes("Importing a module script failed")
	);
};

type RouteErrorBoundaryProps = {
	children: ReactNode;
	resetKey?: string;
};

type RouteErrorBoundaryState = {
	hasError: boolean;
	error: Error | null;
	resetKey?: string;
};

class RouteErrorBoundary extends Component<
	RouteErrorBoundaryProps,
	RouteErrorBoundaryState
> {
	state: RouteErrorBoundaryState = {
		hasError: false,
		error: null,
		resetKey: this.props.resetKey,
	};

	static getDerivedStateFromProps(
		props: RouteErrorBoundaryProps,
		state: RouteErrorBoundaryState,
	): Partial<RouteErrorBoundaryState> | null {
		if (props.resetKey !== state.resetKey) {
			return {
				hasError: false,
				error: null,
				resetKey: props.resetKey,
			};
		}
		return null;
	}

	static getDerivedStateFromError(error: Error): Partial<RouteErrorBoundaryState> {
		return { hasError: true, error };
	}

	componentDidCatch(error: Error, errorInfo: ErrorInfo) {
		console.error("RouteErrorBoundary caught an error:", error, errorInfo);
	}

	handleRefresh = () => {
		window.location.reload();
	};

	render() {
		if (this.state.hasError && this.state.error) {
			const chunkError = isChunkLoadError(this.state.error);

			return (
				<ErrorContainer role="alert">
					<ErrorTitle>
						{chunkError ? "This page failed to load" : "Something went wrong"}
					</ErrorTitle>
					<ErrorMessage>
						{chunkError
							? "The app was updated or your connection was interrupted. Refresh the page to load the latest version."
							: "An unexpected error occurred while loading this page. Try refreshing, or go back and try again."}
					</ErrorMessage>
					<Button type="button" onClick={this.handleRefresh}>
						Refresh page
					</Button>
				</ErrorContainer>
			);
		}

		return this.props.children;
	}
}

export default RouteErrorBoundary;
